'use client';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useDrawer } from '@repo/ui/src/modules/UI';
import { LinkProps } from '@repo/ui/src/types/link';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useBasket from '@hooks/basket/useBasket';
import createCurrencyString from '@repo/ui/src/functions/currency-functions';
import LoadingLogo from '@components/Loading/LoadingLogo';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import useGoToCheckout from '@hooks/basket/useGoToCheckout';
import { useCustomer } from '@hooks/customer';

interface Props {
  isLoading: boolean;
  isEmpty: boolean;
  disabled?: boolean;
  loadingCheckout?: boolean;
  setIsAuthStep?: (value: boolean) => void;
  navigateToCheckout: () => void;
}

const CartFooter = ({
  isLoading,
  isEmpty,
  disabled,
  loadingCheckout,
  setIsAuthStep,
  navigateToCheckout,
}: Props) => {
  const { close, inDrawer } = useDrawer();
  const { trigger: goToCheckout, isMutating } = useGoToCheckout();
  const { data: user } = useCustomer();
  const { data: { total = 0 } = {} } = useBasket();
  const { locale, currency } = useCurrentUrl();
  const { t } = useTranslation();

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      close?.();
    },
    [close],
  );

  const continueToCheckout = React.useCallback(async () => {
    try {
      // if there's no user wait until the auth step is finished to create session
      // in case the user logs in
      // this throws an error if there are out stock items skipping the next steps
      const checkAuth = !user && !!setIsAuthStep;
      await goToCheckout({ skipSession: checkAuth });

      if (!checkAuth) {
        navigateToCheckout();
      } else {
        setIsAuthStep(true);
      }
    } catch {
      /* empty */
    }
  }, [goToCheckout, user, navigateToCheckout, setIsAuthStep]);
  if (isLoading) {
    return null;
  }

  const cartEmptyCTA = t<LinkProps>('cart.cartEmptyCTA.link', undefined, {
    returnObjects: true,
    default: undefined,
  });

  return (
    <>
      {isEmpty ? (
        cartEmptyCTA &&
        inDrawer && (
          <Button
            variant={ButtonVariant.secondary}
            className="tw-w-full tw-rounded tw-text-center"
            onClick={handleClick}
            as={Anchor}
            dataTestId="cart-footer-continue-shopping"
            {...cartEmptyCTA}
          >
            {cartEmptyCTA.displayName}
          </Button>
        )
      ) : (
        <>
          <div className="tw-flex tw-flex-col tw-pb-5">
            <div className="tw-flex tw-justify-between tw-align-text-top">
              <h6 className="tra-text-web-heading-5 sm:tra-text-web-heading-6 tw-text-left">
                {t('cart.estimatedTotal', undefined, { default: 'Estimated Total' })}:
              </h6>
              <h6 className="tra-text-web-heading-5 sm:tra-text-web-heading-6">
                {createCurrencyString(locale, currency, Number(total))}
              </h6>
            </div>
          </div>
          <Button
            type="submit"
            onClick={continueToCheckout}
            disabled={disabled || loadingCheckout || isMutating}
            full
            data-testid="cart-footer-continue-to-checkout"
          >
            {loadingCheckout || isMutating ? (
              <LoadingLogo small />
            ) : (
              `${t('cart.continueToCheckoutCTA', undefined, {
                default: 'Continue to Checkout',
              })}`
            )}
          </Button>
        </>
      )}
    </>
  );
};

export default CartFooter;
